import React, { useRef, useState, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

export interface DraggablePanelProps {
  children: React.ReactNode;
  title?: string;
  defaultHidden?: boolean;
  initialRight?: number;
  initialTop?: string;
  panelWidth?: number;
  zIndex?: number;
  onHide?: (hidden: boolean) => void;
}

export default function DraggablePanel({
  children,
  title,
  defaultHidden = false,
  initialRight = 16,
  initialTop = '50%',
  panelWidth = 170,
  zIndex = 1200,
  onHide,
}: DraggablePanelProps) {
  const [isHidden, setIsHidden] = useState(defaultHidden);
  const [isDragging, setIsDragging] = useState(false);
  const [isFloated, setIsFloated] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  const handleDragStart = (e: React.PointerEvent) => {
    if (!panelRef.current) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    if (!isFloated) {
      const rect = panelRef.current.getBoundingClientRect();
      panelRef.current.style.position = 'fixed';
      panelRef.current.style.left = `${rect.left}px`;
      panelRef.current.style.top = `${rect.top}px`;
      panelRef.current.style.right = 'auto';
      panelRef.current.style.bottom = 'auto';
      panelRef.current.style.transform = 'none';
      setIsFloated(true);
    }

    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: panelRef.current.offsetLeft,
      startTop: panelRef.current.offsetTop,
    };
    e.preventDefault();
  };

  const handleDragMove = (e: PointerEvent) => {
    if (!isDragging || !panelRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const newLeft = Math.max(
      0,
      Math.min(
        window.innerWidth - panelRef.current.offsetWidth - 16,
        dragRef.current.startLeft + dx,
      ),
    );
    const newTop = Math.max(
      0,
      Math.min(
        window.innerHeight - panelRef.current.offsetHeight - 16,
        dragRef.current.startTop + dy,
      ),
    );
    panelRef.current.style.left = `${newLeft}px`;
    panelRef.current.style.top = `${newTop}px`;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handleDragMove);
      window.addEventListener('pointerup', handleDragEnd);
      return () => {
        window.removeEventListener('pointermove', handleDragMove);
        window.removeEventListener('pointerup', handleDragEnd);
      };
    }
  }, [isDragging]);

  const toggleHide = () => {
    const next = !isHidden;
    setIsHidden(next);
    onHide?.(next);
  };

  return (
    <>
      {/* Toggle button — always visible */}
      <Tooltip title={isHidden ? 'Show filters' : 'Hide filters'} placement="left">
        <IconButton
          onClick={toggleHide}
          sx={{
            position: 'fixed',
            top: isFloated ? undefined : initialTop,
            transform: isFloated ? 'none' : 'translateY(-50%)',
            zIndex: zIndex + 1,
            width: 32,
            height: 32,
            bgcolor: 'rgba(255,255,255,0.96)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: 2,
            '&:hover': { bgcolor: '#FFF' },
            transition: 'all 0.2s ease',
            ...(isHidden ? {} : isFloated ? { right: 600, top: 16, transform: 'none' } : {}),
          }}
        >
          {isHidden ? (
            <VisibilityIcon sx={{ fontSize: 16, color: '#9B958F' }} />
          ) : (
            <VisibilityOffIcon sx={{ fontSize: 16, color: '#9B958F' }} />
          )}
        </IconButton>
      </Tooltip>

      {/* The actual panel */}
      {!isHidden && (
        <Box
          ref={panelRef}
          sx={{
            position: 'fixed',
            transform: isFloated ? 'none' : 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            zIndex,
            p: 1.25,
            left: 65,
            top: '60%',
            width: panelWidth,
            bgcolor: 'rgba(255,255,255,0.96)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.08)',
            transition: isDragging ? 'none' : 'all 0.2s ease',
          }}
        >
          {/* Drag handle header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' },
              touchAction: 'none',
              mx: -0.5,
              mt: -0.25,
              mb: 0.25,
              px: 0.5,
              py: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
            }}
            onPointerDown={handleDragStart}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <DragIndicatorIcon sx={{ fontSize: 14, color: '#B0AAA2', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '0.5rem',
                    fontWeight: 700,
                    color: '#B0AAA2',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    lineHeight: 1.1,
                  }}
                >
                  Drag me
                </Typography>
                {title && (
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      fontWeight: 600,
                      color: '#9B958F',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      lineHeight: 1.2,
                    }}
                  >
                    {title}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {children}
        </Box>
      )}
    </>
  );
}
